/**
 * CampaignCanvas — the persistent right-hand campaign panel.
 *
 * Always-current, inline-editable projection of the campaign: chat carries
 * intent, this canvas carries state. Renders the panel for the viewed stage
 * (completed stages are viewable read-only via the stage rail) and hosts the
 * flash animation for patch-driven changes.
 */

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { HeaderStatus } from '@/components/layout/AppLayout';
import { cn } from '@/lib/utils';
import { channelName, goalName, money } from '@/components/growth-desk/data/status';
import { StageRail } from './StageRail';
import { StrategyPanel } from './StrategyPanel';
import { CreativePanel } from './CreativePanel';
import { LaunchPanel } from './LaunchPanel';
import { LivePanel } from './LivePanel';
import { STAGES, type Stage, type Gate, type CampaignState } from './types';
import type { CampaignCanvasApi } from './useCampaignCanvas';

export interface CampaignCanvasProps {
  canvas: CampaignCanvasApi;
  onApprove: (gate: Gate, launchConfig?: Record<string, unknown>) => Promise<string | void> | void;
  conversationId?: string;
  loading?: boolean;
  isDemo?: boolean;
  uploadedImages?: Array<{ url: string }>;
  className?: string;
  /** The chat side panel (campaign page board): shown/hidden from this header. */
  chatOpen?: boolean;
  onToggleChat?: () => void;
  /** Ask the campaign chat for a change (the Launch step's "Change something"). */
  onAskChat?: (text: string) => void;
}

/** "Meta ads · Leads", "₹4,000 · 14 days" — from the campaign's own state. */
const headerChips = (state: CampaignState): string[] => {
  const chans = Object.keys(state.budget?.split ?? {}).map(channelName).filter(Boolean);
  const goal = state.objective ? goalName(String(state.objective).replace(/^OUTCOME_/i, '').toLowerCase()) : '';
  const b = state.budget;
  const money_ = b?.total ? money(b.total, b.currency || 'INR') : '';
  return [
    [chans.join(' · '), goal].filter(Boolean).join(' · '),
    [money_, b?.durationDays ? `${b.durationDays} days` : ''].filter(Boolean).join(' · '),
  ].filter(Boolean);
};

export const CampaignCanvas: React.FC<CampaignCanvasProps> = ({
  canvas,
  onApprove,
  conversationId,
  loading = false,
  isDemo = false,
  uploadedImages,
  className,
  chatOpen,
  onToggleChat,
  onAskChat,
}) => {
  const { stage, state, submitEdit, approving, isFlashing } = canvas;
  const [viewStage, setViewStage] = useState<Stage>(stage);

  // Follow the live stage whenever it advances.
  useEffect(() => {
    setViewStage(stage);
  }, [stage]);

  // Launch panel stays interactive when the campaign is live so users can
  // publish to additional platforms. All other panels are readonly once past.
  const launchReadonly = viewStage !== stage && !(stage === 'live' && viewStage === 'launch');
  const readonly = viewStage !== stage || stage === 'live';
  const viewingPast = STAGES.indexOf(viewStage) < STAGES.indexOf(stage);

  const renderPanel = () => {
    switch (viewStage) {
      case 'brief':
        if (loading) {
          return (
            <div className="space-y-3">
              {/* Plan metrics skeleton */}
              <div className="rounded-lg border border-border bg-card p-4">
                <div className="canvas-shimmer mb-3 h-3 w-10 rounded" />
                <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
                  {[...Array(4)].map((_, i) => (
                    <div key={i} className="rounded-md bg-surface p-2.5 space-y-1.5">
                      <div className="canvas-shimmer h-2 w-12 rounded" />
                      <div className="canvas-shimmer h-3 w-16 rounded" />
                    </div>
                  ))}
                </div>
              </div>
              {/* Budget split skeleton */}
              <div className="rounded-lg border border-border bg-card p-4">
                <div className="canvas-shimmer mb-3 h-3 w-20 rounded" />
                <div className="canvas-shimmer h-2.5 w-full rounded-full" />
                <div className="mt-1.5 flex justify-between">
                  <div className="canvas-shimmer h-2 w-14 rounded" />
                  <div className="canvas-shimmer h-2 w-14 rounded" />
                </div>
              </div>
              {/* Messaging skeleton */}
              <div className="rounded-lg border border-border bg-card p-4">
                <div className="canvas-shimmer mb-3 h-3 w-16 rounded" />
                <div className="space-y-2">
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="rounded-md border border-border p-2.5 space-y-1.5">
                      <div className="canvas-shimmer h-2 w-24 rounded" />
                      <div className="canvas-shimmer h-3 w-full rounded" />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          );
        }
        return (
          <div className="rounded-lg border-2 border-dashed border-border p-10 text-center text-sm leading-relaxed text-ink-3">
            <p className="mb-1 text-[15px] text-ink-2">Your campaign builds here as you chat</p>
            Strategy, creatives, budget and targeting will appear in this panel — always
            current, always editable.
          </div>
        );
      case 'strategy':
        return (
          <StrategyPanel
            state={state}
            readonly={readonly}
            onEdit={submitEdit}
            onApprove={onApprove}
            approving={approving}
            isFlashing={isFlashing}
          />
        );
      case 'creative':
        return (
          <CreativePanel
            state={state}
            readonly={readonly}
            onEdit={submitEdit}
            onApprove={onApprove}
            approving={approving}
            isFlashing={isFlashing}
            conversationId={conversationId}
            onRefresh={canvas.refresh}
            uploadedImages={uploadedImages}
          />
        );
      case 'launch':
        return (
          <LaunchPanel
            state={state}
            readonly={launchReadonly}
            onEdit={submitEdit}
            onApprove={onApprove}
            approving={approving}
            conversationId={conversationId}
            stage={stage}
            isDemo={isDemo}
            onAskChat={onAskChat}
          />
        );
      case 'live':
        return <LivePanel state={state} conversationId={conversationId} />;
      default:
        return null;
    }
  };

  return (
    <section
      className={cn('flex h-full min-h-0 flex-col overflow-hidden bg-ground', className)}
      aria-label="Campaign canvas"
    >
      {/* Local flash + shimmer animations for patch/generation states */}
      <style>{`
        @keyframes canvasFlash {
          0% { background-color: rgb(var(--c-accent-soft, 253 243 221)); }
          100% { background-color: transparent; }
        }
        .canvas-flash { animation: canvasFlash 1.2s ease; }
        @keyframes canvasShimmer {
          from { background-position: 200% 0; }
          to { background-position: -200% 0; }
        }
        .canvas-shimmer {
          background: linear-gradient(90deg,
            rgb(var(--c-border)) 25%,
            rgb(var(--c-card-alt)) 50%,
            rgb(var(--c-border)) 75%);
          background-size: 200% 100%;
          animation: canvasShimmer 1.3s infinite linear;
        }
      `}</style>

      <header className="flex flex-col gap-4 px-5 pb-2 pt-5 md:px-10 md:pt-6">
        <div className="hidden flex-wrap items-center justify-between gap-3 md:flex">
          <nav aria-label="Breadcrumb" className="flex min-w-0 gap-2 text-small text-ink-3">
            <Link to="/campaigns" className="text-ink-2 hover:text-ink hover:underline">Campaigns</Link>
            <span aria-hidden="true">/</span>
            <span className="truncate">{(state.name as string) ?? 'New campaign'}</span>
          </nav>
          <div className="hidden items-center gap-2 md:flex">
            <HeaderStatus />
            {onToggleChat && (
              <button type="button" onClick={onToggleChat} aria-pressed={!!chatOpen}
                className="inline-flex min-h-[40px] items-center rounded-btn px-3.5 font-display text-sm font-bold text-ink shadow-ring-line hover:shadow-ring-ink">
                {chatOpen ? 'Hide chat' : 'Chat with assistant'}
              </button>
            )}
          </div>
        </div>
        <div className="hidden flex-col gap-2.5 md:flex">
          <div className="flex flex-wrap gap-1.5">
            {isDemo && <span className="inline-flex h-6 items-center rounded-full bg-brand-soft px-2.5 text-xs font-semibold text-brand-ink">Demo</span>}
            {stage === 'live'
              ? <span className="inline-flex h-6 items-center gap-1.5 rounded-full bg-ok px-2.5 text-xs font-semibold text-card">● Live</span>
              : <span className="inline-flex h-6 items-center rounded-full bg-inset px-2.5 text-xs font-semibold text-ink-2">Draft · not spending yet</span>}
            {headerChips(state).map(c => <span key={c} className="inline-flex h-6 items-center rounded-full bg-inset px-2.5 text-xs font-semibold text-navy-ink">{c}</span>)}
          </div>
          <h1 className="m-0 font-display text-[30px] font-extrabold leading-none tracking-[-0.035em] text-ink md:text-display-m">
            {(state.name as string) ?? 'New campaign'}
          </h1>
        </div>
        <StageRail stage={stage} viewStage={viewStage} onViewStage={setViewStage} />
      </header>

      {viewingPast && (
        <div className="flex items-center gap-2 border-b border-border bg-surface px-4 py-1.5 text-[11px] text-ink-3">
          <span>
            Viewing the approved <span className="font-medium text-ink-2">{viewStage}</span> stage (read-only)
          </span>
          <button
            type="button"
            className="ml-auto font-medium text-primary underline-offset-2 hover:underline"
            onClick={() => setViewStage(stage)}
          >
            ← Back to {stage}
          </button>
        </div>
      )}

      {canvas.error && (
        <div className="flex items-center gap-2 border-b border-border bg-bad-soft px-4 py-2 text-xs text-bad">
          <span className="flex-1">{canvas.error}</span>
          <button type="button" onClick={canvas.clearError} className="font-medium">
            Dismiss
          </button>
        </div>
      )}

      <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-6 pt-4 md:px-10">{renderPanel()}</div>
    </section>
  );
};
